import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  registerExtensionEditorFormat,
  registerExtensionsEditorSetup,
} from 'src/app/features/mce-editor/registerExtensions';
import { IonList, IonItem } from '@ionic/angular/standalone';
import { QuotationService } from 'src/app/features/mce-editor/extensions/quotation/quotation.service';
import { operationExtension } from 'src/app/features/mce-editor/extensions/operation/operation.extension';
import { speechExtension } from 'src/app/features/mce-editor/extensions/speech/speech.extension';
import { TranslateService } from '@ngx-translate/core';
import { TINYMCE_BASE_URL } from 'src/app/features/mce-editor/tinymce.constants';
import { I18nService } from 'src/app/features/i18n/i18n.service';

@Component({
  selector: 're-chat-field',
  providers: [QuotationService],
  imports: [CommonModule, FormsModule, EditorComponent, IonList, IonItem],
  template: `
    @let entities = filteredQuotationEntity();
    @if (entities) {
      <div class="w-full">
        <ion-list class="ion-no-padding" mode="md">
          @for (entity of entities; track entity.id) {
            <ion-item
              [button]="true"
              (click)="
                selectQuotation(entity.id, entity.displayName, entity.type)
              "
              >{{ entity.displayName }}</ion-item
            >
          }
        </ion-list>
      </div>
    }
    @if (editorVisible()) {
      <editor
        licenseKey="gpl"
        [init]="config()"
        class="flex-1"
        [class.hidden]="!ready()"
        [class.editor-mini]="type() === 'mini'"
        [class.editor-full]="type() === 'full'"
        [(ngModel)]="text"
      />
    }
  `,
  styles: [':host{display: contents;}'],
})
export class ChatFieldComponent {
  private readonly editorComponent = viewChild(EditorComponent);
  injector = inject(Injector);
  quotation = inject(QuotationService);
  private translate = inject(TranslateService);
  private i18n = inject(I18nService);
  type = input<'full' | 'mini'>('full');
  allowOperation = input<boolean>(true);
  placeholder = input<string>();
  /** Incremented when the WebView returns from background to rebuild TinyMCE. */
  refreshVersion = input(0);
  quotationEntity =
    input<{ id: string; displayName: string; type: 'user' | 'char' }[]>();
  filteredQuotationEntity = computed(() => {
    return this.quotation.currentInput()
      ? this.quotationEntity()?.filter((u) =>
          u.displayName
            .toLowerCase()
            .includes(this.quotation.currentInput()?.toLowerCase() ?? ''),
        )
      : [];
  });
  text = model();
  ready = signal<boolean>(false);
  /** Kept public because the Angular template controls the editor lifetime. */
  editorVisible = signal(true);
  private lastRefreshVersion = 0;

  constructor() {
    effect(() => {
      const version = this.refreshVersion();
      if (version === 0 || version === this.lastRefreshVersion) return;
      this.lastRefreshVersion = version;
      this.recreateEditor();
    });
  }
  config = computed<EditorComponent['init']>(() => {
    return {
      base_url: TINYMCE_BASE_URL,
      branding: false,
      statusbar: false,
      extended_valid_elements: 'div[data-speech|class],svg[*],path[*]',
      body_class:
        this.type() === 'mini' ? 're-prose re-prose--mini' : 're-prose',
      toolbar: ['undo operation bold speech'],
      ...(this.i18n.currentLang() === 'it'
        ? {
            language: 'it',
            language_url: '/assets/tinymce/langs/it.js',
          }
        : {}),
      placeholder:
        this.placeholder()?.trim() ||
        this.translate.instant('chat.writePlaceholder'),
      license_key: 'gpl',
      formats: {
        ...registerExtensionEditorFormat([
          operationExtension.format,
          speechExtension.format,
        ]),
      },
      setup: (editor) => {
        editor.on('init', (e) => {
          this.ready.set(true);
        });
        registerExtensionsEditorSetup(editor, this.injector, [
          ...(this.allowOperation() ? [operationExtension.setup] : []),
          speechExtension.setup,
        ]);
      },
      ...(this.type() === 'full'
        ? {
            content_css: [
              '/tinymce-styles/default.css',
              '/tinymce-styles/prose.css',
              '/tinymce-styles/extensions.css',
              '/tinymce-styles/full-editor.css',
            ],
            plugins: '',
            min_height: 300,
          }
        : {
            height: 0,
            content_css: [
              '/tinymce-styles/default.css',
              '/tinymce-styles/prose.css',
              '/tinymce-styles/extensions.css',
              '/tinymce-styles/mini-editor.css',
            ],
            plugins: 'autoresize',
            autoresize_bottom_margin: 5,
            min_height: 60,
            max_height: 300,
          }),
    };
  });

  selectQuotation(id: string, value: string, type: 'user' | 'char') {
    this.quotation.selectValue({ id, type, value });
  }

  /**
   * A suspended WebView can leave TinyMCE's iframe alive but detach its event
   * handlers. Capture its draft first, then recreate only this editor.
   */
  private recreateEditor(): void {
    try {
      const content = this.editorComponent()?.editor?.getContent();
      if (typeof content === 'string') this.text.set(content);
    } catch {
      // A discarded iframe cannot be queried; ngModel still holds the draft.
    }

    this.ready.set(false);
    this.editorVisible.set(false);
    window.setTimeout(() => this.editorVisible.set(true));
  }
}
